import { Locale } from '@headless-workspace/config';
import { Repository, Result, ResultFailureFromApiError, ResultSuccess } from '@headless-workspace/core-domain';
import {
    memoryCacheAuthStrategy,
    memoryCacheQueryParamStrategy,
    memoryCacheRefreshTokenStrategy,
} from '@headless-workspace/data';
import { VotingType } from '../../types';
import { RatingsAndReviewsDTO } from './RatingsAndReviewsDTO';
import { RatingsAndReviewsValue, mapRatingsAndReviews } from './RatingsAndReviewsValue';

const enum FeedbackTypeValue {
    Helpfulness = 'helpfulness',
    Report = 'inappropriate',
}

export interface ReviewsRepositorySpec {
    getReviews(
        locale: Locale,
        productId: string,
        offset: number,
        limit: number,
        filters?: string,
        sort?: string,
    ): Promise<Result<RatingsAndReviewsValue>>;

    evaluateReview(locale: Locale, reviewId: string, vote: VotingType): Promise<Result<void>>;

    reportReview(locale: Locale, reviewId: string): Promise<Result<void>>;
}

export class ReviewsRepository extends Repository implements ReviewsRepositorySpec {
    path = '/catalog/v1/reviews';

    constructor() {
        super(memoryCacheAuthStrategy, memoryCacheRefreshTokenStrategy, memoryCacheQueryParamStrategy);
    }

    async getReviews(
        locale: Locale,
        productId: string,
        offset: number,
        limit: number,
        filters?: string,
        sort?: string,
    ): Promise<Result<RatingsAndReviewsValue>> {
        try {
            const params: Record<string, unknown> = {
                locale,
                productId,
                offset,
                limit,
            };

            if (filters) params.filters = filters;
            if (sort) params.sort = sort;

            const response = await this.datasource.getResource(this.path, params);

            const ratingsAndReviews = RatingsAndReviewsDTO.parse(response);

            return ResultSuccess(mapRatingsAndReviews(ratingsAndReviews, locale));
        } catch (error) {
            const apiError = this.handleApiError(error);
            return ResultFailureFromApiError(apiError, { locale });
        }
    }

    async evaluateReview(locale: Locale, reviewId: string, vote: VotingType): Promise<Result<void>> {
        try {
            const params = {
                locale,
            };
            const body = {
                vote,
                type: FeedbackTypeValue.Helpfulness,
            };

            void this.datasource.createResource(`${this.path}/${reviewId}/feedback`, body, params);
            return ResultSuccess(void 0);
        } catch (error) {
            const apiError = this.handleApiError(error);
            return ResultFailureFromApiError(apiError, { locale });
        }
    }

    async reportReview(locale: Locale, reviewId: string): Promise<Result<void>> {
        try {
            const params = {
                locale,
            };
            const body = {
                type: FeedbackTypeValue.Report,
            };

            void this.datasource.createResource(`${this.path}/${reviewId}/feedback`, body, params);
            return ResultSuccess(void 0);
        } catch (error) {
            const apiError = this.handleApiError(error);
            return ResultFailureFromApiError(apiError, { locale });
        }
    }
}
